/** The entries that a phone keeps until the server has them (design 10.2, 10.6). */

import { SERVER_PROBLEM } from "../save";
import { keys, read, remove, type Scope, write, writeUnless } from "./db";
import { canChange, FIELD_TEXT, fieldText, type RefusedField, refusedField, serverDetail } from "./messages";
import { askPersist, capProblem, dropBytes, dropLostBytes, keepBytes, photoBlob } from "./photoStore";

export type Kind = "start" | "load" | "pickup" | "visit" | "stop" | "finish" | "photo";
export type State = "saved" | "sending" | "sent" | "failed";
/** What started a pass, for the log. */
export type Trigger = "start" | "online" | "timer" | "new" | "button" | "again";

export interface OutboxEntry {
  id: string;
  kind: Kind;
  method: "POST";
  path: string;
  body: Record<string, unknown>;
  route_id: string | null;
  label: string;
  state: State;
  reason: string | null;
  /** The server text of a refused send, for support on the Sync page. */
  detail?: string | null;
  /** The form field that the refusal names, so that the form can mark it after a reload. */
  field?: RefusedField | null;
  /** A pickup that the server has already as a copy: it is on the server, not Not saved. */
  copy?: boolean;
  tries: number;
  created_at: number;
  sent_at: number | null;
}

export type NewEntry = Pick<OutboxEntry, "id" | "kind" | "path" | "body" | "label"> & { route_id?: string | null };

/** The send sequence in each pass: checks, loads, the stops and visits as made, the end odometers, then the photos. */
export const KIND_ORDER: Record<Kind, number> = { start: 0, load: 1, pickup: 2, visit: 2, stop: 2, finish: 3, photo: 4 };
export const SENT_KEEP_MS = 24 * 3_600_000;
export const RETRY_MS = 60_000;
export const RETRY_AFTER_MAX_MS = 15 * 60_000;
export const POST_TIMEOUT_MS = 30_000;
export const PUT_TIMEOUT_MS = 5 * 60_000;
export const TOKEN_TIMEOUT_MS = 15_000;
export const NO_SIGNAL = "No signal. It is sent when the phone is online.";
export const UPLOAD_AGAIN = "The upload did not finish. The phone tries again.";
export const NO_BYTES = "The photo is not on this phone any more.";

export const SECOND_PICKUP = "The pickup of this stop is saved already. The phone did not save the gallons again. Tap Mark it done.";

export const NO_STORAGE = "Not saved. This browser does not let the app keep data on the phone. Turn on the site data, or leave the private window.";

export const STATE_TEXT: Record<State, string> = { saved: "Saved on phone", sending: "Sending", sent: "Sent", failed: "Not saved" };
export const ON_SERVER = "On the server";
export const stateText = (entry: Pick<OutboxEntry, "state" | "copy">) => (entry.copy ? ON_SERVER : STATE_TEXT[entry.state]);

/** A refused pickup that the server has as a copy gets one state on each page: on the server. */
const serverCopy = (entry: OutboxEntry, reason: string | null) => entry.kind === "pickup" && reason === FIELD_TEXT.copy;

const PREFIX = "outbox:";

export const waiting = (entry: OutboxEntry) => entry.state === "saved" || entry.state === "sending";
export const unsent = (entry: OutboxEntry) => entry.state !== "sent";

export function sendOrder(a: OutboxEntry, b: OutboxEntry): number {
  return KIND_ORDER[a.kind] - KIND_ORDER[b.kind] || a.created_at - b.created_at || a.id.localeCompare(b.id);
}

export interface Pass {
  sent: number;
  signIn: boolean;
}

export interface Change {
  entry: OutboxEntry;
  result?: unknown;
}

type Listener = (change: Change | null) => void;
const listeners = new Map<string, Set<Listener>>();
const signIns = new Map<string, boolean>();
const running = new Map<string, Promise<Pass>>();
const again = new Set<string>();
let noLocksTold = false;
const pauses = new Map<string, number>();
let lastMade = 0;

export function subscribe(scope: Scope, listener: Listener): () => void {
  const set = listeners.get(scope.prefix) ?? new Set<Listener>();
  set.add(listener);
  listeners.set(scope.prefix, set);
  return () => set.delete(listener);
}

const tell = (scope: Scope, change: Change | null) => listeners.get(scope.prefix)?.forEach((listener) => listener(change));

/** The time before which a pass waits, from the Retry-After of the last 503 or 429. */
export const pausedUntil = (scope: Scope) => pauses.get(scope.prefix) ?? 0;

/** True after a 401, until a send succeeds. */
export const signInNeeded = (scope: Scope) => signIns.get(scope.prefix) === true;

const save = (scope: Scope, entry: OutboxEntry) => write(scope.key(`${PREFIX}${entry.id}`), entry);

export async function entries(scope: Scope): Promise<OutboxEntry[]> {
  const names = await keys(scope.key(PREFIX));
  const found = await Promise.all(names.map((name) => read<OutboxEntry>(name)));
  return found.flatMap((kept) => (kept ? [kept.value] : [])).sort(sendOrder);
}

/** The entries of each user on this phone that wait to be sent. */
export async function waitingOnPhone(): Promise<number> {
  const names = (await keys("user:")).filter((name) => name.includes(`:${PREFIX}`));
  const found = await Promise.all(names.map((name) => read<OutboxEntry>(name)));
  return found.filter((kept) => kept && waiting(kept.value)).length;
}

/** The pickup of the same route and account that is on the phone, sent, or a copy on the server: a route stop has one pickup. */
export const routePickup = (all: readonly OutboxEntry[], made: Pick<NewEntry, "kind" | "path" | "route_id">) =>
  made.kind !== "pickup" || !made.route_id
    ? undefined
    : all.find((old) => old.kind === "pickup" && old.route_id === made.route_id && old.path === made.path && (old.state !== "failed" || !canChange(old.reason)));

/** Keep a new entry. A Not saved entry for the same path is the one that the driver fixes, so it goes. Visits and photos keep it. */
export async function enqueue(scope: Scope, made: NewEntry, now: number = Date.now()): Promise<OutboxEntry> {
  lastMade = Math.max(now, lastMade + 1);
  const entry: OutboxEntry = {
    ...made,
    route_id: made.route_id ?? null,
    method: "POST",
    state: "saved",
    reason: null,
    tries: 0,
    created_at: lastMade,
    sent_at: null,
  };
  const all = made.kind === "visit" || made.kind === "photo" ? [] : await entries(scope);
  const held = routePickup(all, made);
  if (held) {
    console.warn(`outbox: pickup ${made.id} is blocked, pickup ${held.id} of the same route stop is ${held.state} (route ${made.route_id})`);
    throw new Error(SECOND_PICKUP);
  }
  const fixed = all.filter((old) => old.state === "failed" && old.path === made.path);
  await remove(fixed.map((old) => scope.key(`${PREFIX}${old.id}`)));
  await save(scope, entry);
  if (!(await read<OutboxEntry>(scope.key(`${PREFIX}${entry.id}`)))) throw new Error(NO_STORAGE);
  tell(scope, { entry });
  return entry;
}

/** Only a Not saved entry can go; a waiting entry may already be on the server. */
export async function discard(scope: Scope, id: string): Promise<boolean> {
  const kept = await read<OutboxEntry>(scope.key(`${PREFIX}${id}`));
  if (!kept || kept.value.state !== "failed") return false;
  await remove([scope.key(`${PREFIX}${id}`)]);
  if (kept.value.kind === "photo") await dropBytes(scope, [id]);
  tell(scope, null);
  return true;
}

/** The photos with bytes on the phone: each photo that the server did not store yet. */
export const heldPhotos = (all: readonly OutboxEntry[]) =>
  all.filter((entry) => entry.kind === "photo" && entry.state !== "sent").map((entry) => ({ id: entry.id, size: Number(entry.body.size_bytes) || 0 }));

/** Keep the bytes first, then the entry. The bytes go only after /stored succeeds or a discard. */
export async function keepPhoto(scope: Scope, made: NewEntry, blob: Blob, now: number = Date.now()): Promise<OutboxEntry> {
  const full = capProblem(heldPhotos(await entries(scope)), blob.size);
  if (full) throw new Error(full);
  if (!(await keepBytes(scope, made.id, blob))) throw new Error(NO_STORAGE);
  void askPersist();
  try {
    return await enqueue(scope, made, now);
  } catch (error) {
    await dropBytes(scope, [made.id]);
    throw error;
  }
}

async function prune(scope: Scope, all: OutboxEntry[], now: number): Promise<void> {
  for (const entry of all.filter((found) => found.state === "failed" && serverCopy(found, found.reason))) {
    await saveResult(scope, { ...entry, state: "sent", copy: true, reason: null, field: null, sent_at: now });
    console.info(`outbox: pickup ${entry.id} is on the server as a copy, so it is not Not saved`);
  }
  const old = all.filter((entry) => entry.state === "sent" && entry.sent_at !== null && now - entry.sent_at > SENT_KEEP_MS);
  await remove(old.map((entry) => scope.key(`${PREFIX}${entry.id}`)));
  await dropLostBytes(scope, new Set(all.filter((entry) => entry.kind === "photo").map((entry) => entry.id)), now);
}

async function detailOf(response: Response): Promise<string | null> {
  return serverDetail(await response.json().catch(() => null));
}

type Step =
  | { next: "go"; result: unknown }
  | { next: "stop"; reason: string; signIn?: boolean; waitMs?: number }
  | { next: "failed"; reason: string; detail: string | null; field?: RefusedField | null };

/** Retry-After in seconds, as milliseconds, at most RETRY_AFTER_MAX_MS. */
export function retryAfterMs(response: Response): number | undefined {
  const seconds = Number(response.headers.get("Retry-After"));
  return Number.isFinite(seconds) && seconds > 0 ? Math.min(seconds * 1000, RETRY_AFTER_MAX_MS) : undefined;
}

/** The entry, the try and the trigger of one send, for the log. No customer data. */
interface Sending {
  kind: Kind;
  id: string;
  tries: number;
  trigger: Trigger;
}

const logSend = (send: Sending, what: string, status: number | string, started: number) =>
  console.info(`outbox send: ${send.kind} ${send.id} try ${send.tries} (${send.trigger}) ${what} -> ${status} in ${Date.now() - started} ms at ${new Date().toISOString()}`);

/** A fetch that stops after `ms`, so that a send never holds the lock for ever. Null is no answer. */
async function timed(url: string, init: RequestInit, ms: number, send: Sending, what: string): Promise<Response | null> {
  const started = Date.now();
  const abort = new AbortController();
  const timer = setTimeout(() => abort.abort(), ms);
  try {
    const response = await fetch(url, { ...init, signal: abort.signal });
    logSend(send, what, response.status, started);
    return response;
  } catch {
    logSend(send, what, abort.signal.aborted ? "timeout" : "no answer", started);
    return null;
  } finally {
    clearTimeout(timer);
  }
}

const TOKEN_LATE = new Error("token timeout");

/** The token, or a rejection after TOKEN_TIMEOUT_MS, so that a token that never comes ends the pass. */
function tokenWithin(token: () => Promise<string>): Promise<string> {
  let timer: ReturnType<typeof setTimeout> | undefined;
  const late = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(TOKEN_LATE), TOKEN_TIMEOUT_MS);
  });
  return Promise.race([token(), late]).finally(() => clearTimeout(timer));
}

async function post(path: string, body: unknown, token: () => Promise<string>, send: Sending): Promise<Response | null> {
  const asked = Date.now();
  let auth: string;
  try {
    auth = await tokenWithin(token);
  } catch (error) {
    logSend(send, `POST ${path}`, error === TOKEN_LATE ? "token timeout" : "no token", asked);
    return null;
  }
  const init: RequestInit = {
    method: "POST",
    headers: { Authorization: `Bearer ${auth}`, "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  };
  return timed(`/api${path}`, init, POST_TIMEOUT_MS, send, `POST ${path}`);
}

async function stepOf(response: Response | null, kind?: Kind): Promise<Step> {
  if (!response) return { next: "stop", reason: NO_SIGNAL };
  if (response.ok) return { next: "go", result: await response.json().catch(() => null) };
  if (response.status === 401) return { next: "stop", reason: "Sign in again to send it.", signIn: true };
  if (response.status >= 500 || response.status === 408 || response.status === 429) return { next: "stop", reason: SERVER_PROBLEM, waitMs: retryAfterMs(response) };
  const detail = (await detailOf(response)) ?? `HTTP ${response.status}`;
  return { next: "failed", reason: fieldText(response.status, detail, kind), detail, field: refusedField(response.status, detail) };
}

const sendOne = async (entry: OutboxEntry, token: () => Promise<string>, send: Sending) => stepOf(await post(entry.path, entry.body, token, send), entry.kind);

/** A new signed URL at each try, the PUT, then /stored. A 409 at the start can come from a stored photo with no answer. */
async function sendPhoto(scope: Scope, entry: OutboxEntry, token: () => Promise<string>, send: Sending): Promise<Step> {
  const blob = await photoBlob(scope, entry.id);
  if (!blob) return { next: "failed", reason: NO_BYTES, detail: null };
  const storedPath = `/photos/${encodeURIComponent(entry.id)}/stored`;
  const started = await post(entry.path, entry.body, token, send);
  if (started?.status === 409) {
    const stored = await post(storedPath, undefined, token, send);
    if (stored?.ok) return stepOf(stored);
  }
  const step = await stepOf(started);
  if (step.next !== "go") return step;
  const ticket = step.result as { url?: string; headers?: Record<string, string> } | null;
  if (!ticket?.url) return { next: "stop", reason: SERVER_PROBLEM };
  const put = await timed(ticket.url, { method: "PUT", headers: ticket.headers ?? {}, body: blob }, PUT_TIMEOUT_MS, send, "PUT photo bytes");
  if (!put) return { next: "stop", reason: NO_SIGNAL };
  if (!put.ok) return { next: "stop", reason: UPLOAD_AGAIN };
  return stepOf(await post(storedPath, undefined, token, send));
}

/** Each pass writes its counts to the console, so a pass on a phone is never blind. */
async function logPass(scope: Scope, sent: number, refused: number, stopped: string | null): Promise<void> {
  const kept = (await entries(scope)).filter(waiting).length;
  console.info(`outbox pass: ${sent} sent, ${refused} refused, ${kept} kept${stopped ? ` (stopped: ${stopped})` : ""}`);
}

/** Write the entry, unless a different tab has it as Sent already: Sent never goes back. */
async function saveResult(scope: Scope, entry: OutboxEntry): Promise<boolean> {
  const written = await writeUnless<OutboxEntry>(scope.key(`${PREFIX}${entry.id}`), entry, (old) => old.state === "sent" && entry.state !== "sent");
  if (!written) console.warn(`outbox: ${entry.kind} ${entry.id} is Sent already, so the ${entry.state} state is not kept`);
  return written;
}

type Ran = Pass & { stopped: boolean };

async function pass(scope: Scope, token: () => Promise<string>, now: () => number, trigger: Trigger): Promise<Ran> {
  await prune(scope, await entries(scope), now());
  const tried = new Set<string>();
  let sent = 0;
  let refused = 0;
  for (;;) {
    const entry = (await entries(scope)).find((found) => waiting(found) && !tried.has(found.id));
    if (!entry) break;
    tried.add(entry.id);
    const sending: OutboxEntry = { ...entry, state: "sending", tries: entry.tries + 1 };
    if (!(await saveResult(scope, sending))) continue;
    tell(scope, { entry: sending });
    const send: Sending = { kind: sending.kind, id: sending.id, tries: sending.tries, trigger };
    const step = sending.kind === "photo" ? await sendPhoto(scope, sending, token, send) : await sendOne(sending, token, send);
    if (step.next === "stop") {
      const back: OutboxEntry = { ...sending, state: "saved", reason: step.reason };
      if (await saveResult(scope, back)) tell(scope, { entry: back });
      if (step.signIn) signIns.set(scope.prefix, true);
      if (step.waitMs) pauses.set(scope.prefix, now() + step.waitMs);
      await logPass(scope, sent, refused, step.reason);
      return { sent, signIn: Boolean(step.signIn), stopped: true };
    }
    signIns.set(scope.prefix, false);
    pauses.delete(scope.prefix);
    const copy = step.next === "failed" && serverCopy(sending, step.reason);
    const done: OutboxEntry =
      step.next === "go"
        ? { ...sending, state: "sent", reason: null, detail: null, field: null, sent_at: now() }
        : copy
          ? { ...sending, state: "sent", copy: true, reason: null, detail: step.detail, field: null, sent_at: now() }
          : { ...sending, state: "failed", reason: step.reason, detail: step.detail, field: step.field ?? null };
    if (copy) console.warn(`outbox: pickup ${done.id} got 409, the server has it as a copy, so it is on the server`);
    else if (step.next === "failed") console.warn(`outbox: ${done.kind} ${done.id} is Not saved (field ${done.field ?? "none"}): ${step.detail ?? step.reason}`);
    if (!(await saveResult(scope, done))) continue;
    if (step.next === "go" && done.kind === "photo") await dropBytes(scope, [done.id]);
    if (step.next === "go" || copy) sent += 1;
    else refused += 1;
    tell(scope, step.next === "go" ? { entry: done, result: step.result } : { entry: done });
  }
  await logPass(scope, sent, refused, null);
  return { sent, signIn: false, stopped: false };
}

/** One sender at a time over all the tabs and windows of this browser, by a Web Lock on the scope. */
function exclusive<T>(scope: Scope, work: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === "undefined" ? undefined : navigator.locks;
  if (!locks?.request) {
    if (!noLocksTold) console.warn("outbox: this browser has no Web Locks, so only this tab is kept to one sender");
    noLocksTold = true;
    return work();
  }
  return locks.request(`xflow-outbox:${scope.prefix}`, { mode: "exclusive" }, work);
}

/** Send the waiting entries one at a time, in order. A call during a pass joins it and asks for one more pass. */
export function sendAll(scope: Scope, token: () => Promise<string>, now: () => number = Date.now, trigger: Trigger = "button"): Promise<Pass> {
  const busy = running.get(scope.prefix);
  if (busy) {
    again.add(scope.prefix);
    console.info(`outbox: a pass runs, so the ${trigger} trigger joins it`);
    return busy;
  }
  const work = (async (): Promise<Pass> => {
    let next: Trigger = trigger;
    for (;;) {
      again.delete(scope.prefix);
      const ran = await exclusive(scope, () => pass(scope, token, now, next));
      if (ran.stopped || !again.has(scope.prefix)) return { sent: ran.sent, signIn: ran.signIn };
      next = "again";
    }
  })().finally(() => {
    running.delete(scope.prefix);
    again.delete(scope.prefix);
    tell(scope, null);
  });
  running.set(scope.prefix, work);
  return work;
}

/** The time of the last sent entry, from the kept entries. */
export const lastSent = (all: readonly OutboxEntry[]) => all.reduce<number | null>((last, entry) => (entry.sent_at !== null && (last === null || entry.sent_at > last) ? entry.sent_at : last), null);

/** The number on the header badge: each entry that is not on the server yet. */
export const badgeCount = (all: readonly OutboxEntry[]) => all.filter(unsent).length;

/** The label and tooltip of the header Sync badge, for example "4 entries not sent". */
export function badgeText(count: number, bad: boolean): string {
  return `${count} ${count === 1 ? "entry" : "entries"} not sent${bad ? ", some not saved" : ""}`;
}

/** For tests: forget the listeners, the passes and the sign-in flags. */
export function resetOutboxForTests(): void {
  listeners.clear();
  signIns.clear();
  running.clear();
  again.clear();
  pauses.clear();
  noLocksTold = false;
  lastMade = 0;
}
